// BlurredChartPlaceholder.jsx
import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import './BlurredChartPlaceholder.css';
import { getRandomLoadingMessage } from '../../components/QuirkyLoadingMessages/QuirkyLoadingMessages';

const BlurredChartPlaceholder = ({ type = 'line', message = '' }) => {
  // State for the loading message
  const [loadingMessage, setLoadingMessage] = useState(getRandomLoadingMessage());
  
  // Set up message rotation
  useEffect(() => {
    const intervalId = setInterval(() => {
      if( message.length > 0 ){
        setLoadingMessage(message);
      } else {
        setLoadingMessage(getRandomLoadingMessage());
      }
    }, 21000); // Change message every 3.5 seconds
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [message]);
  
  // Color scheme matching your existing charts
  const chartColors = {
    positive: '#A6FCA1', // green for 'yes'
    negative: '#EA6B62', // red for 'no'
    scales: {
      '1.0': '#EA6B62',
      '2.0': '#FCDAD1',
      '3.0': '#DFE6C1',
      '4.0': '#C2F0B1',
      '5.0': '#A6FCA1'
    },
    areas: {
      happy: 'rgba(50, 205, 50, 0.5)',
      unhappy: 'rgba(255, 0, 0, 0.5)'
    }
  };

  // Generate plausible looking data for placeholder
  const generateRandomData = (min = 30, max = 70, count = 10) => {
    const data = [];
    let value = Math.floor(Math.random() * (max - min) + min);
    
    for (let i = 0; i < count; i++) {
      // Create somewhat smoothed data with slight variations
      const change = Math.floor(Math.random() * 10) - 5;
      value = Math.max(min, Math.min(max, value + change));
      data.push(value);
    }
    return data;
  };

  const generateRandomDates = (count = 10) => {
    const dates = [];
    const date = new Date();
    for (let i = 0; i < count; i++) {
      date.setDate(date.getDate() - 1);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates.reverse();
  };

  // Different options based on chart type
  const getOptions = () => {
    const dates = generateRandomDates();
    
    if (type === 'line') {
      // Simulate your line chart with yes/no responses over time
      // Also include the happy/unhappy area markers
      const yesData = generateRandomData(60, 90);
      const noData = generateRandomData(10, 40);
      
      // Create a plausible "peak" period for demonstration
      const happyStart = Math.floor(dates.length * 0.3);
      const happyEnd = Math.floor(dates.length * 0.5);
      const unhappyStart = Math.floor(dates.length * 0.7);
      const unhappyEnd = Math.floor(dates.length * 0.9);
      
      return {
        title: {
          text: 'Loading chart data...',
          textStyle: {
            opacity: 0.5
          }
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: { show: true, opacity: 0.2 },
          axisLine: { show: true, opacity: 0.2 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          axisLabel: { show: true, opacity: 0.2 },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: true, lineStyle: { opacity: 0.1 } }
        },
        series: [
          {
            name: 'Yes',
            data: yesData,
            type: 'line',
            smooth: true,
            symbol: 'none',
            lineStyle: { width: 2 },
            itemStyle: {
              color: chartColors.positive
            }
          },
          {
            name: 'No',
            data: noData,
            type: 'line',
            smooth: true,
            symbol: 'none',
            lineStyle: { width: 2 },
            itemStyle: {
              color: chartColors.negative
            }
          },
          {
            type: 'line',
            data: [dates[0], dates[dates.length-1]],
            markArea: {
              itemStyle: {
                color: chartColors.areas.happy
              },
              data: [
                [
                  {
                    name: "Happy Peak",
                    xAxis: dates[happyStart]
                  },
                  {
                    xAxis: dates[happyEnd]
                  }
                ]
              ]
            },
            z: 100
          },
          {
            type: 'line',
            data: [dates[0], dates[dates.length-1]],
            markArea: {
              itemStyle: {
                color: chartColors.areas.unhappy
              },
              data: [
                [
                  {
                    name: "Unhappy Peak",
                    xAxis: dates[unhappyStart]
                  },
                  {
                    xAxis: dates[unhappyEnd]
                  }
                ]
              ]
            },
            z: 100
          }
        ],
        grid: {
          left: '5%', 
          right: '5%',
          top: '15%',
          bottom: '10%'
        },
        tooltip: { show: false }
      };
    } else if (type === 'bar') {
      // Simulate your bar chart with yes/no responses
      return {
        title: {
          text: 'Loading chart data...',
          textStyle: {
            opacity: 0.5
          }
        },
        xAxis: {
          type: 'category',
          data: ['Yes', 'No'],
          axisLabel: { show: true, opacity: 0.3 },
          axisLine: { show: true, opacity: 0.2 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          axisLabel: { show: true, opacity: 0.2 },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: true, lineStyle: { opacity: 0.1 } }
        },
        series: [
          {
            data: [
              {
                value: 70,
                itemStyle: { color: chartColors.positive }
              },
              {
                value: 30,
                itemStyle: { color: chartColors.negative }
              }
            ],
            type: 'bar'
          }
        ],
        grid: {
          left: '5%',
          right: '5%',
          top: '15%',
          bottom: '10%'
        },
        tooltip: { show: false }
      };
    } else if (type === 'pie') {
      // Simulate your pie chart with yes/no responses
      return {
        title: {
          text: 'Loading chart data...',
          textStyle: {
            opacity: 0.5
          }
        },
        series: [
          {
            type: 'pie',
            radius: '60%',
            data: [
              { 
                value: 70, 
                name: 'Yes',
                itemStyle: { color: chartColors.positive }
              },
              { 
                value: 30, 
                name: 'No',
                itemStyle: { color: chartColors.negative }
              }
            ],
            label: { show: false }
          }
        ],
        tooltip: { show: false }
      };
    }
    
    // Default fallback
    return {
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: { show: false },
        axisLine: { show: false }, 
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        axisLabel: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false }
      },
      series: [
        {
          data: generateRandomData(),
          type: 'line',
          smooth: true,
          symbol: 'none'
        }
      ],
      grid: {
        left: '5%',
        right: '5%', 
        top: '15%',
        bottom: '10%'
      },
      tooltip: { show: false }
    };
  };

  return (
    <div className="blurred-chart-placeholder">
      <div className="placeholder-title">{loadingMessage}</div>
      <div className="blurred-chart">
        <ReactECharts 
          option={getOptions()} 
          style={{ 
            height: '100%', // Changed from fixed 300px to 100%
            width: '100%',
            filter: 'blur(2.5px) brightness(0.98)',
            padding: '16px'
          }}
          lazyUpdate={true}
        />
      </div>
      <div className="loading-indicator"></div>
    </div>
  );
};

export default BlurredChartPlaceholder;